import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, CalendarCheck, AlertCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfirmDate = () => {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const id = params.get("id");
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | ready | done | already | error
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id || !token) {
      setState("error");
      return;
    }
    axios
      .get(`${API}/public/prebooking/${id}?token=${token}`)
      .then((res) => {
        setInfo(res.data);
        setState(res.data.status === "confirmed" ? "already" : "ready");
      })
      .catch(() => setState("error"));
  }, [id, token]);

  const accept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/accept-proposed-date`, { id, token });
      setState("done");
    } catch (e) {
      setState("error");
    }
    setBusy(false);
  };

  const c = {
    fr: {
      title: "Confirmer la nouvelle date",
      activity: "Activité",
      date: "Nouvelle date proposée",
      time: "Créneau",
      people: "Personnes",
      confirm: "Je confirme cette date",
      doneTitle: "Date confirmée, merci !",
      doneText: "Votre réservation est confirmée pour la nouvelle date. Vous allez recevoir un email récapitulatif.",
      alreadyTitle: "Déjà confirmée",
      alreadyText: "Cette réservation est déjà confirmée.",
      errorTitle: "Lien invalide",
      errorText: "Ce lien de confirmation est invalide ou a expiré. Contactez-nous sur WhatsApp au +212 6 68 97 83 63.",
      home: "Retour à l'accueil",
    },
    en: {
      title: "Confirm the new date",
      activity: "Activity",
      date: "Proposed new date",
      time: "Time slot",
      people: "People",
      confirm: "I confirm this date",
      doneTitle: "Date confirmed, thank you!",
      doneText: "Your booking is confirmed for the new date. You'll receive a summary email shortly.",
      alreadyTitle: "Already confirmed",
      alreadyText: "This booking is already confirmed.",
      errorTitle: "Invalid link",
      errorText: "This confirmation link is invalid or expired. Contact us on WhatsApp at +212 6 68 97 83 63.",
      home: "Back to home",
    },
  }[lang];

  return (
    <div className="min-h-screen bg-[#f7f5ef] flex items-center justify-center px-5 pt-24 pb-16">
      <div className="max-w-lg w-full bg-white rounded-3xl border border-[#eceae1] p-10 md:p-12 text-center shadow-[0_25px_60px_-30px_rgba(28,34,30,0.35)]">
        {state === "loading" && <Loader2 size={44} className="text-[#7c8f74] mx-auto animate-spin" />}

        {state === "ready" && info && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#7c8f74]/15 flex items-center justify-center mx-auto mb-6">
              <CalendarCheck size={30} className="text-[#5c6b54]" />
            </div>
            <h1 className="font-serif text-3xl text-[#20261f] mb-6">{c.title}</h1>
            <div className="text-left bg-[#f7f5ef] rounded-xl p-5 mb-7 space-y-2">
              <p className="flex justify-between"><span className="text-[#9aa08f] text-sm">{c.activity}</span><span className="font-medium text-[#20261f]">{info.activity_title}</span></p>
              <p className="flex justify-between"><span className="text-[#9aa08f] text-sm">{c.date}</span><span className="font-medium text-[#20261f]">{info.proposed_date}</span></p>
              {info.proposed_time && <p className="flex justify-between"><span className="text-[#9aa08f] text-sm">{c.time}</span><span className="font-medium text-[#20261f]">{info.proposed_time}</span></p>}
              <p className="flex justify-between"><span className="text-[#9aa08f] text-sm">{c.people}</span><span className="font-medium text-[#20261f]">{info.people}</span></p>
            </div>
            <button
              onClick={accept}
              disabled={busy}
              className="w-full bg-[#7c8f74] hover:bg-[#697c62] text-white rounded-full py-4 text-sm tracking-[0.1em] uppercase font-medium transition disabled:opacity-60"
            >
              {busy ? "…" : c.confirm}
            </button>
          </>
        )}

        {(state === "done" || state === "already") && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#7c8f74] flex items-center justify-center mx-auto mb-6">
              <Check size={32} className="text-white" />
            </div>
            <h1 className="font-serif text-3xl text-[#20261f] mb-4">
              {state === "done" ? c.doneTitle : c.alreadyTitle}
            </h1>
            <p className="text-[#6b7166] leading-relaxed mb-8">
              {state === "done" ? c.doneText : c.alreadyText}
            </p>
            <Link to="/" className="inline-flex items-center gap-2 bg-[#7c8f74] hover:bg-[#697c62] text-white px-6 py-3 rounded-full text-sm tracking-wide transition">
              {c.home}
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#f0e7e3] flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={30} className="text-[#b98b6e]" />
            </div>
            <h1 className="font-serif text-3xl text-[#20261f] mb-4">{c.errorTitle}</h1>
            <p className="text-[#6b7166] leading-relaxed mb-8">{c.errorText}</p>
            <Link to="/" className="inline-flex items-center gap-2 border border-[#dedbd0] text-[#3d443a] px-6 py-3 rounded-full text-sm tracking-wide hover:border-[#7c8f74] transition">
              {c.home}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmDate;
